/**
 * OAuth Authentication Action Hooks
 *
 * These hooks provide high-level OAuth authentication actions
 * that convert Stytch responses to our domain types.
 */
import { discoveryOrganizationTimestamps } from './discovery-organization-timestamps';
import { useCallback } from 'react';
import { useStytchWrappers } from './stytch-hooks';
/**
 * Hook to start Google OAuth flow
 */
export function useStartGoogleOAuth() {
    const { startGoogleOAuthDiscovery } = useStytchWrappers();
    return useCallback(async (options) => {
        try {
            await startGoogleOAuthDiscovery(options);
            return {
                success: true,
                data: { redirected: true },
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to start Google OAuth',
                data: error,
            };
        }
    }, [startGoogleOAuthDiscovery]);
}
/**
 * Hook to start Microsoft OAuth flow
 */
export function useStartMicrosoftOAuth() {
    const { startMicrosoftOAuthDiscovery } = useStytchWrappers();
    return useCallback(async (options) => {
        try {
            await startMicrosoftOAuthDiscovery(options);
            return {
                success: true,
                data: { redirected: true },
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to start Microsoft OAuth',
                data: error,
            };
        }
    }, [startMicrosoftOAuthDiscovery]);
}
/**
 * Hook to start Google One Tap for specific organization
 */
export function useStartGoogleOneTap() {
    const { startGoogleOneTap } = useStytchWrappers();
    return useCallback(async (options) => {
        try {
            const result = await startGoogleOneTap(options);
            return {
                success: true,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to start Google One Tap',
                data: error,
            };
        }
    }, [startGoogleOneTap]);
}
/**
 * Hook to start Google One Tap discovery flow
 */
export function useStartGoogleOneTapDiscovery() {
    const { startGoogleOneTapDiscovery } = useStytchWrappers();
    return useCallback(async (options) => {
        try {
            const result = await startGoogleOneTapDiscovery(options);
            return {
                success: true,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to start Google One Tap Discovery',
                data: error,
            };
        }
    }, [startGoogleOneTapDiscovery]);
}
/**
 * Hook to authenticate OAuth discovery callback
 */
export function useAuthenticateOAuthDiscovery() {
    const { authenticateOAuthDiscovery } = useStytchWrappers();
    return useCallback(async (token) => {
        const result = await authenticateOAuthDiscovery(token);
        if (result.success) {
            return {
                success: true,
                intermediateSessionToken: result.intermediate_session_token,
                emailAddress: result.email_address,
                discoveredOrganizations: result.discovered_organizations?.map(org => ({
                    id: org.organization.organization_id,
                    name: org.organization.organization_name,
                    domain: org.organization.organization_slug || '',
                    member_count: 0, // Would need to fetch this separately
                    ...discoveryOrganizationTimestamps(org.organization),
                    stytch_organization_id: org.organization.organization_id,
                    membershipType: org.membership?.type,
                })),
                hasExistingOrganization: result.discovered_organizations && result.discovered_organizations.length > 0,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [authenticateOAuthDiscovery]);
}
/**
 * Hook to authenticate OAuth callback
 */
export function useAuthenticateOAuth() {
    const { authenticateOAuth } = useStytchWrappers();
    return useCallback(async (token, options) => {
        const result = await authenticateOAuth(token, options);
        if (result.success && result.member && result.organization) {
            // Convert Stytch member to our UserProfile type
            const userProfile = {
                id: result.member.member_id, // This should ideally be our database user ID
                email: result.member.email_address,
                name: result.member.name || '',
                role: 'member', // Default role, should be extracted from JWT or database
                workspace_id: result.organization.organization_id,
                created_at: result.member.created_at,
                updated_at: result.member.updated_at,
                stytch_member_id: result.member.member_id,
                stytch_organization_id: result.organization.organization_id,
                permissions: [], // Should be extracted from JWT or database
                roles: [], // Should be extracted from JWT or database
            };
            // Convert Stytch organization to our Workspace type
            const workspace = {
                id: result.organization.organization_id,
                name: result.organization.organization_name,
                description: undefined,
                company_logo_path: result.organization.organization_logo_url,
                domain: result.organization.organization_slug,
                company_domain: result.organization.organization_slug,
                number_of_users: 0, // Would need to fetch this separately
                plan_type: 'free', // Default, should be fetched from database
                is_active: true,
                subscription_amount: undefined,
                subscription_status: undefined,
                trial_start_date: undefined,
                trial_end_date: undefined,
                current_period_end: undefined,
                seat_count: undefined,
                ...discoveryOrganizationTimestamps(result.organization),
                created_by: result.member.member_id,
                country: undefined,
                stytch_organization_id: result.organization.organization_id,
                last_active_at: undefined,
                is_annual_billing: undefined,
                stripe_customer_id: undefined,
                stripe_subscription_id: undefined,
            };
            return {
                success: true,
                session: result.session,
                userProfile,
                workspace,
                // Propagate MFA / intermediate-session signals so the callback page can route
                // into MFA verification or org selection rather than a dead-end ensure failure.
                intermediateSessionToken: result.intermediate_session_token,
                mfaRequired: result.mfa_required,
                // Raw member/org ids the callback page's MFA handler needs to build
                // `mfa_required_data` (userProfile/workspace rename these fields, so they
                // can't be read back as member_id/organization_id). Present on both the
                // fully-authenticated and MFA-required responses.
                memberId: result.member.member_id,
                organizationId: result.organization.organization_id,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error,
                data: result.data,
            };
        }
    }, [authenticateOAuth]);
}
